import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { Loader2, LogOut, RotateCw } from "lucide-react";
import { Logo } from "@/components/Logo";
import { http, errorText } from "@/lib/api";

const TABS = [["requests", "Anfragen"], ["bookings", "Termine"], ["blocks", "Zeiten sperren"], ["emails", "E-Mail-Protokoll"]];
const viennaTime = (iso) => new Date(iso).toLocaleString("de-AT", { timeZone: "Europe/Vienna", dateStyle: "medium", timeStyle: "short" });

function Login({ onDone }) {
  const [email, setEmail] = useState(""); const [pw, setPw] = useState(""); const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await http.post("/auth/login", { email, password: pw }); onDone(); } catch (x) { setErr(errorText(x, "Anmeldung fehlgeschlagen.")); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mx-auto mt-20 max-w-[380px] space-y-4 rounded-2xl border border-line bg-white p-8" data-testid="admin-login-form">
      <h1 className="font-display text-2xl font-semibold">Verwaltung</h1>
      <label className="block text-[14px] font-semibold">E-Mail<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" className="mt-1 h-12 w-full rounded-xl border border-line px-4 font-normal" autoComplete="username" /></label>
      <label className="block text-[14px] font-semibold">Passwort<input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="admin-login-password" className="mt-1 h-12 w-full rounded-xl border border-line px-4 font-normal" autoComplete="current-password" /></label>
      {err && <p role="alert" className="text-[14px] text-[#B42318]" data-testid="admin-login-error">{err}</p>}
      <button className="btn btn-ink w-full" disabled={busy} data-testid="admin-login-submit">{busy ? "…" : "Anmelden"}</button>
    </form>
  );
}

function Table({ cols, rows, empty }) {
  if (!rows.length) return <p className="py-8 text-mute">{empty}</p>;
  return (
    <div className="overflow-x-auto rounded-xl border border-line bg-white">
      <table className="w-full min-w-[720px] text-left text-[13px]">
        <thead className="bg-paper text-mute"><tr>{cols.map(([l]) => <th key={l} className="px-3 py-2 font-semibold">{l}</th>)}</tr></thead>
        <tbody>{rows.map((r) => <tr key={r.id} className="border-t border-line align-top">{cols.map(([l, fn]) => <td key={l} className="px-3 py-2">{fn(r)}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function Blocks() {
  const [day, setDay] = useState(format(new Date(), "yyyy-MM-dd"));
  const [slots, setSlots] = useState([]); const [err, setErr] = useState("");
  const load = useCallback(() => { http.get("/admin/slots", { params: { date: day } }).then(({ data }) => setSlots(data)).catch((e) => setErr(errorText(e, "Fehler"))); }, [day]);
  useEffect(() => { load(); }, [load]);
  const toggle = async (s) => {
    setErr("");
    try {
      if (s.taken?.kind === "block") await http.delete(`/admin/blocks/${s.taken.id}`);
      else await http.post("/admin/blocks", { start: s.start });
      load();
    } catch (e) { setErr(errorText(e, "Fehler")); }
  };
  return (
    <div>
      <label className="text-[14px] font-semibold">Datum <input type="date" value={day} onChange={(e) => setDay(e.target.value)} data-testid="admin-block-date" className="ml-2 h-10 rounded-lg border border-line px-3" /></label>
      <p className="mt-2 text-[13px] text-mute">Klick auf eine freie Zeit sperrt sie; Klick auf eine Sperre gibt sie frei. Gebuchte Termine stornieren Sie im Reiter „Termine“. Zeiten: Europe/Vienna.</p>
      {err && <p role="alert" className="mt-2 text-[#B42318]">{err}</p>}
      <div className="mt-4 grid grid-cols-4 gap-2 sm:grid-cols-8">
        {slots.map((s) => {
          const k = s.taken?.kind;
          return (
            <button key={s.start} type="button" disabled={s.past || k === "booking"} onClick={() => toggle(s)} data-testid={`admin-slot-${s.label.replace(/\W/g, "")}`}
              className={`h-10 rounded-lg border text-[13px] font-semibold ${k === "block" ? "border-[#B42318] bg-[#FEF3F2] text-[#B42318]" : k === "booking" ? "border-gold bg-gold-soft" : s.past ? "border-transparent bg-black/5 text-black/30" : "border-line bg-white hover:border-ink"}`}>
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Admin() {
  const [auth, setAuth] = useState(null);
  const [tab, setTab] = useState("requests");
  const [data, setData] = useState({ requests: [], bookings: [], emails: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Verwaltung – Webhaus";
    const m = document.createElement("meta"); m.name = "robots"; m.content = "noindex,nofollow"; document.head.appendChild(m);
    http.get("/auth/me").then(() => setAuth(true)).catch(() => setAuth(false));
    return () => { m.remove(); };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, b, e] = await Promise.all([http.get("/admin/demo-requests"), http.get("/admin/bookings"), http.get("/admin/emails")]);
      setData({ requests: r.data, bookings: b.data, emails: e.data });
    } catch (x) { if (x?.response?.status === 401) setAuth(false); } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (auth) load(); }, [auth, load]);

  const cancel = async (id) => { if (window.confirm("Termin wirklich stornieren? Der Besucher wird benachrichtigt.")) { await http.post(`/admin/bookings/${id}/cancel`); load(); } };
  const retry = async (id) => { await http.post(`/admin/emails/${id}/retry`); load(); };
  const logout = async () => { await http.post("/auth/logout"); setAuth(false); };

  if (auth === null) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!auth) return <div className="wrap"><div className="pt-6"><Logo /></div><Login onDone={() => setAuth(true)} /></div>;

  return (
    <div className="wrap py-6" data-testid="admin-dashboard">
      <div className="flex items-center justify-between"><Logo /><div className="flex gap-2">
        <button type="button" onClick={load} className="btn btn-line !min-h-[40px]" data-testid="admin-refresh"><RotateCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />Aktualisieren</button>
        <button type="button" onClick={logout} className="btn btn-line !min-h-[40px]" data-testid="admin-logout"><LogOut className="h-4 w-4" />Abmelden</button></div></div>
      <div className="no-scrollbar mt-8 flex gap-2 overflow-x-auto">
        {TABS.map(([k, l]) => <button key={k} type="button" onClick={() => setTab(k)} data-testid={`admin-tab-${k}`} className={`shrink-0 rounded-full px-4 py-2 text-[14px] font-semibold ${tab === k ? "bg-ink text-paper" : "border border-line bg-white"}`}>{l}</button>)}
      </div>
      <div className="mt-6">
        {tab === "requests" && <Table empty="Noch keine Anfragen." rows={data.requests} cols={[["Eingang", (r) => viennaTime(r.created_at)], ["Name", (r) => r.name], ["Unternehmen", (r) => <>{r.company}<br /><span className="text-mute">{r.industry}</span></>], ["Kontakt", (r) => <>{r.email}<br />{r.phone}</>], ["Status", (r) => (r.has_website === "none" ? "Keine Website" : <>Erneuern<br /><span className="break-all text-mute">{r.website}</span></>)], ["Beispiel", (r) => r.design_example || "–"], ["Wünsche", (r) => <span className="whitespace-pre-line">{r.wishes || "–"}</span>]]} />}
        {tab === "bookings" && <Table empty="Noch keine Termine." rows={data.bookings} cols={[["Termin (Wien)", (r) => <>{r.local_date}<br />{r.local_time} {r.tz_label}</>], ["Name", (r) => r.name], ["Kontakt", (r) => <>{r.email}<br />{r.phone}</>], ["Thema", (r) => r.topic || "–"], ["Status", (r) => (r.status === "active" ? "aktiv" : "storniert")], ["", (r) => r.status === "active" && <button type="button" className="font-semibold text-[#B42318] underline" onClick={() => cancel(r.id)} data-testid={`admin-cancel-${r.id}`}>Stornieren</button>]]} />}
        {tab === "blocks" && <Blocks />}
        {tab === "emails" && <Table empty="Noch keine E-Mails." rows={data.emails} cols={[["Erstellt", (r) => r.created_at?.slice(0, 16)], ["Art", (r) => r.kind], ["An", (r) => r.to], ["Betreff", (r) => r.subject], ["Status", (r) => <span className={r.status === "sent" ? "text-[#067647]" : r.status === "failed" ? "text-[#B42318]" : ""}>{r.status}{r.error && <><br /><span className="text-[11px]">{r.error}</span></>}</span>], ["", (r) => r.status !== "sent" && <button type="button" className="font-semibold underline" onClick={() => retry(r.id)} data-testid={`admin-retry-${r.id}`}>Erneut senden</button>]]} />}
      </div>
    </div>
  );
}
